<template>
  <div class="micro-app">
    <!-- Header -->
    <div class="header">
      <div class="header-title">Subscription</div>
    </div>

    <!-- Scrollable Content -->
    <div class="content">
      <!-- Section 1: License Plan -->
      <div class="section">
        <div class="section-header">
          <span class="section-title">1. License Plan</span>
          <!-- <span class="tag-optional">Optional</span> -->
        </div>

        <div class="card">
          <div class="card-header">
            <div class="card-left">
              <div class="check-box">
                <el-checkbox
                  :value="hasLicense"
                  :disabled="isPendingCancel"
                  @change="onCheckboxChange"></el-checkbox>
                <span class="card-title">{{ (subscriptionPlan && subscriptionPlan.name) || 'Subscription TVU License' }}</span>
              </div>
              <p
                class="description"
                v-html="(subscriptionPlan && subscriptionPlan.description) || 'Unlock premium rates and exclusive features. Automatically renews every month.'"></p>
              <div class="card-tag">
                <el-tag>
                  <span class="el-icon-date icon"></span>
                  <span>{{ capitalizeFirstLetter(subscriptionRecurring) }} Subscription</span>
                </el-tag>
                <el-tag v-if="!isPendingCancel">
                  <span class="el-icon-refresh icon"></span>
                  <span>Auto-renewal</span>
                </el-tag>
                <el-tag
                  v-if="isPendingCancel"
                  type="warning">
                  <span class="el-icon-warning-outline icon"></span>
                  <span>Cancelled - will not renew next month</span>
                </el-tag>
              </div>
            </div>
            <div class="card-right">
              <div class="price-block">
                <div class="price-font">${{ subscriptionPrice }}</div>
                <div class="price-unit">per {{ subscriptionRecurring }}</div>
              </div>
              <el-button
                class="price-btn"
                :class="{ 'is-active': hasLicense }"
                :disabled="isPendingCancel"
                @click="currentSubscription && !isPendingCancel ? handleCancelSubscription() : toggleLicense()">
                {{ isPendingCancel ? 'Subscription Cancelled' : currentSubscription ? 'Cancel Subscription' : (hasLicense ? 'Remove License' : 'Add License') }}
              </el-button>
            </div>
          </div>
          <div class="card-bottom"></div>
        </div>
      </div>

      <!-- Section 2: Token Top-up and Service Rates -->
      <div class="section">
        <div class="section-header">
          <span class="section-title">2. Token Top-up and Service Rates</span>
        </div>

        <div class="card">
          <div class="card-header">
            <div class="card-left">
              <div class="check-box">
                <i class="iconfont icon-token"></i>
                <span class="card-title">{{ (tokenPlan && tokenPlan.name) || 'Purchase Tokens' }}</span>
              </div>
              <p
                class="description"
                v-html="(tokenPlan && tokenPlan.description) || 'Usage rates are charged from your token balance.'"></p>
              <div class="card-tag">
                <el-tag>
                  <span class="el-icon-date icon"></span>
                  <span>Valid for {{ tokenValidity }} {{ tokenValidityUnit }} from last purchase</span>
                </el-tag>
                <el-tag>
                  <span class="el-icon-refresh icon"></span>
                  <span>One-time fee</span>
                </el-tag>
              </div>
            </div>
            <div class="card-right">
              <div class="purchase-token">
                <div class="token-count">
                  <el-input-number
                    v-model="tokenCount"
                    size="small"
                    :min="0"
                    :max="100"
                    :step="1"
                    step-strictly></el-input-number>
                </div>
                <div class="token-price">
                  <span class="single-price">${{ tokenUnitPrice }}</span>
                  <span> / {{ tokenAmount }} tokens</span>
                </div>
                <div class="token-total">${{ tokenUnitPrice }} x {{ tokenCount }} unit</div>
              </div>
            </div>
          </div>
          <div class="card-bottom">
            <div class="auto-recharge">
              <div class="recharge-operate">
                <el-switch
                  :value="autoRecharge"
                  active-color="#2fb54e"
                  inactive-color="#595959"
                  @change="onAutoRechargeChange">
                </el-switch>
                <div class="recharge-info">
                  <div class="recharge-title">Auto-recharge</div>
                  <div class="recharge-text">Automatically buy tokens when balance is less than 100 tokens.</div>
                </div>
              </div>
              <div class="recharge-tip">{{ autoRecharge ? 'Will auto-recharge when low balance.' : 'Manual top-up required later.' }}</div>
            </div>
            <div class="service-price-list">
              <div
                class="list-title"
                @click="servicePriceCollapsed = !servicePriceCollapsed">
                <i class="iconfont icon-tag"></i>
                <span class="card-title">Service Price</span>
                <span
                  v-if="hasLicense"
                  class="service-price-discount-badge">{{ discountPercent }}% OFF</span>
                <i
                  class="collapse-icon"
                  :class="servicePriceCollapsed ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
              </div>

              <div
                v-show="!servicePriceCollapsed"
                class="price-table">
                <div class="table-header">
                  <div class="col-app">Micro app</div>
                  <div class="col-rate">Rate</div>
                </div>
                <div
                  class="table-row"
                  v-for="(item, index) in serviceRates"
                  :key="index">
                  <div class="col-app">{{ item.name }}</div>
                  <div class="col-rate">
                    <template v-if="hasLicense">
                      <span class="rate-discounted">{{ getDiscountedPrice(item.rate) }}</span>
                      <span class="rate-unit"> / {{ item.unit }}</span>
                      <span class="rate-original">{{ item.rate }}</span>
                      <span class="discount-badge">{{ discountPercent }}% OFF</span>
                    </template>
                    <template v-else>
                      <span class="rate-bold">{{ item.rate }}</span>
                      <span class="rate-unit"> / {{ item.unit }}</span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer footer-highlight">
      <div class="footer-left">
        <div class="total-payment">
          <div class="label">Total Payment</div>
          <div class="value">${{ totalPayment }}</div>
        </div>
        <div class="line"></div>
        <div class="payment-details">
          <div class="detail-line">
            Service Tokens({{ tokenCount }}x): <span class="total-price">${{ tokenPrice }}</span>
          </div>
          <div
            v-if="hasLicense && !currentSubscription"
            class="detail-line">
            Monthly License: <span class="total-price">${{ subscriptionPrice }}</span>
          </div>
        </div>
        <div class="line"></div>
        <div class="recharge-info">
          <span>
            <span>Token Top-up:</span>
            <span class="white-text">{{ autoRecharge ? 'Auto-recharge enabled when low' : 'Manual top-up required'}}</span>
          </span>
          <span v-show="hasLicense">
            <span>License Renewal:</span>
            <span class="white-text">{{ isPendingCancel ? 'Cancels at end of billing period' : 'Auto-renews ' + subscriptionRecurring + '($' + subscriptionPrice + ')' }}</span>
          </span>
        </div>
      </div>
      <div class="footer-right">
        <el-button
          type="success"
          class="pay-btn"
          :disabled="tokenCount === 0 && (!hasLicense || !!currentSubscription)"
          @click="handlePay">
          <i class="el-icon-bank-card"></i> Pay Now
        </el-button>
      </div>
    </div>

    <el-dialog
      :visible.sync="checkoutVisible"
      :before-close="handleCloseCheckOut"
      :close-on-click-modal="false"
      :destroy-on-close="true"
      custom-class="checkout-dialog">
      <div
        id="checkout"
        ref="checkoutContainer"></div>
    </el-dialog>
  </div>
</template>

<script>
import { getPlans, userHasSubscription } from '@/common/checkuser'
import { isNoPaymentMethodError, NO_PAYMENT_METHOD_MESSAGE } from '@/common/autoRecharge'
import { mapState } from 'vuex'
import { billingApiBase } from '@/api/billingBase'

export default {
  name: 'NewMicro',
  data () {
    return {
      hasLicense: false,
      tokenCount: 1,
      autoRecharge: false,
      serviceRates: [],
      planData: null,
      subscriptionPlan: null,
      tokenPlan: null,
      loading: true,
      checkoutVisible: false,
      checkout: null,
      currentSubscription: null,
      servicePriceCollapsed: true
    }
  },
  computed: {
    ...mapState(['userInfo']),
    emailId () {
      return this.userInfo?.email || ''
    },
    product () {
      return 'MicroApp'
    },
    isPendingCancel () {
      return this.currentSubscription?.pendingCancel === 1
    },

    totalPayment () {
      let total = this.tokenPrice
      if (this.hasLicense && !this.currentSubscription) {
        total += this.subscriptionPlan.price
      }
      return total
    },
    discountMultiplier () {
      return this.subscriptionPlan?.discountMultiplier || 0.25
    },
    subscriptionPrice () {
      return this.subscriptionPlan?.price || 98
    },
    subscriptionRecurring () {
      return this.subscriptionPlan?.recurring || 'monthly'
    },
    tokenUnitPrice () {
      return this.tokenPlan?.price || 77
    },
    tokenPrice () {
      return this.tokenCount * this.tokenUnitPrice
    },
    tokenAmount () {
      return this.tokenPlan?.features?.find(f => f.key === 'tokenAmount')?.value || 1000
    },
    tokenValidity () {
      const validityFeature = this.tokenPlan?.features?.find(f => f.key === 'tokenValidity')
      return validityFeature?.value || 6
    },
    tokenValidityUnit () {
      const validityFeature = this.tokenPlan?.features?.find(f => f.key === 'tokenValidity')
      return validityFeature?.unit || 'months'
    },
    discountPercent () {
      return Math.round(parseFloat(((1 - this.discountMultiplier) * 100).toFixed(10)))
    }
  },
  mounted () {
    this.fetchPlanData()
    this.checkSubscription()
    this.fetchCustomerInfo()
  },
  methods: {
    async checkSubscription () {
      try {
        const res = await userHasSubscription({ product: '_Global', classification: 3 })
        const subscriptions = res.data?.subscription?.subscriptions || []
        const statusList = [0, 3]
        // 过滤为 status 0 3的
        const currentSubscriptions = subscriptions.filter(i => statusList.includes(i.status))
        if (currentSubscriptions.length > 0) {
          this.currentSubscription = currentSubscriptions[0]
          this.hasLicense = true
        }
      } catch (error) {
        console.error('Failed to check subscription:', error)
      }
    },
    async fetchPlanData () {
      try {
        this.loading = true
        const params = {
          customerId: this.emailId,
          product: '_Global',
          classification: 3
        }
        const res = await getPlans(params)

        if (res.data?.plan?.plans) {
          const plans = res.data.plan.plans
          const advancedPlan = plans.find(p => p.id === 'advanced_user_subscription')

          if (advancedPlan && advancedPlan.metadata) {
            const metadata = JSON.parse(advancedPlan.metadata)
            console.log(metadata)

            this.planData = metadata

            if (metadata.plans && metadata.plans.length > 0) {
              this.subscriptionPlan = metadata.plans.find(p => p.recurring) || metadata.plans[0]
              this.tokenPlan = metadata.plans.find(p => !p.recurring) || metadata.plans[1]

              if (this.subscriptionPlan?.features) {
                this.serviceRates = this.subscriptionPlan.features.map(feature => ({
                  name: feature.label,
                  rate: `$${feature.value.toFixed(2)}`,
                  unit: this.capitalizeFirstLetter(feature.unit)
                }))
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch plan data:', error)
        this.setDefaultData()
      } finally {
        this.loading = false
      }
    },
    setDefaultData () {
      this.serviceRates = [
        { name: 'A/V Sync', rate: '$0.20', unit: 'Minute' },
        { name: 'Audio Remapping', rate: '$0.20', unit: 'Minute' },
        { name: 'SCTE', rate: '$0.20', unit: 'Minute' },
        { name: 'Media Analyzer', rate: '$0.50', unit: 'Minute' },
        { name: 'Transcriber', rate: '$0.70', unit: 'Minute' },
        { name: 'Standard Conversion', rate: '$2.00', unit: 'Minute' },
        { name: 'Overlay', rate: '$0.48', unit: 'Minute' },
        { name: 'Test Pattern Generator', rate: '$0.36', unit: 'Minute' },
        { name: 'Color Correction', rate: '$0.48', unit: 'Minute' }
      ]
    },
    capitalizeFirstLetter (str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    toggleLicense () {
      this.hasLicense = !this.hasLicense
    },
    onCheckboxChange (val) {
      if (this.currentSubscription && !val) {
        this.handleCancelSubscription()
      } else {
        this.hasLicense = val
      }
    },
    async handleCancelSubscription () {
      try {
        await this.$confirm(
          'Your subscription will remain active until the end of the current billing period and will not renew next month.',
          'Cancel Subscription',
          {
            confirmButtonText: 'Yes, Cancel',
            cancelButtonText: 'No',
            type: 'warning'
          }
        )
      } catch {
        return
      }
      try {
        const res = await this.$axios.post(`${billingApiBase()}/user/cancelCommitment`, {
          product: '_Global',
          planId: this.currentSubscription.planId
        })
        if (res.data?.errorCode !== '0x0') {
          this.$message.error(res.data?.errorInfo || 'Failed to cancel subscription')
          return
        }
        this.$message.success('Subscription cancelled. It will remain active until the end of the billing period.')
        await this.checkSubscription()
      } catch (error) {
        console.error('Cancel subscription error:', error)
        this.$message.error('Failed to cancel subscription')
      }
    },
    async onAutoRechargeChange (val) {
      const success = await this.updateAutoRecharge(val)
      if (success) {
        this.autoRecharge = val
        this.$message.success(val ? 'Auto-recharge turned on' : 'Auto-recharge turned off')
      }
    },
    async fetchCustomerInfo () {
      try {
        const res = await this.$axios.get(`${billingApiBase()}/user/customerInfo`)
        if (res.data?.errorCode === '0x0') {
          this.autoRecharge = res.data.result?.autoRecharge === 1
        }
      } catch (error) {
        console.error('fetchCustomerInfo failed:', error)
      }
    },
    async updateAutoRecharge (enable) {
      try {
        const res = await this.$axios.get(`${billingApiBase()}/user/updateAutoRecharge`, {
          params: { autoRecharge: enable ? '1' : '0' }
        })
        if (res.data?.errorCode !== '0x0') {
          if (res.data?.errorCode === '80E00132') {
            this.$message && this.$message({ type: 'warning', message: 'Contact Owner/Finance member for your group to Purchase', duration: 3000, showClose: true })
          } else if (enable && isNoPaymentMethodError(res.data?.errorCode)) {
            this.$message && this.$message.error(NO_PAYMENT_METHOD_MESSAGE)
          } else {
            this.$message && this.$message.error(res.data?.errorInfo || 'Failed to update auto-recharge')
          }
          return false
        }
        return true
      } catch (error) {
        console.error('updateAutoRecharge failed:', error)
        this.$message.error('Failed to update auto-recharge')
        return false
      }
    },
    async handlePay () {
      const planId = (!this.currentSubscription && this.hasLicense)
        ? (this.subscriptionPlan?.id || 'advanced_user_subscription')
        : ''
      const url = `${billingApiBase()}/user/stripMixingPaymentClient?quantity=${this.tokenCount}&planId=${planId}`

      const res = await this.$axios.get(url)
      if (res.data?.errorCode !== '0x0') {
        if (res.data?.errorCode === '80E00132') {
          this.$message && this.$message({ type: 'warning', message: 'Contact Owner/Finance member for your group to Purchase', duration: 3000, showClose: true })
        } else {
          this.$message && this.$message.error(res.data?.result || 'Failed to initialize checkout.')
        }
        return
      }
      const clientSecret = res.data.result

      this.checkoutVisible = true
      await this.$nextTick()
      if (this.$refs.checkoutContainer) {
        this.$refs.checkoutContainer.innerHTML = ''
      }

      const stripe = window.Stripe(window.CONFIG.stripePublishKey)
      const checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: () => Promise.resolve(clientSecret),
        onComplete: () => {}
      })
      this.checkout = checkout
      checkout.mount('#checkout')
    },
    handleCloseCheckOut () {
      if (this.checkout) {
        this.checkout.unmount()
        this.checkout.destroy()
        this.checkout = null
      }
      this.checkoutVisible = false
      setTimeout(() => {
        this.checkSubscription()
      }, 1500)
    },
    getDiscountedPrice (rate) {
      const value = parseFloat(rate.replace('$', ''))
      const discounted = value * this.discountMultiplier
      const str = parseFloat(discounted.toFixed(4)).toString()
      return '$' + str
    }
  }
}
</script>

<style lang="less" scoped>
.micro-app {
  width: 800px;
  min-width: 880px;
  min-height: 600px;
  margin: 50px auto;
  display: flex;
  flex-direction: column;
  background-color: #262626;
  border-radius: 10px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;

    .header-title {
      font-style: normal;
      font-weight: 600;
      font-size: 18px;
      color: #f8f8f8;
    }
  }

  .content {
    padding: 0px 20px;

    .section {
      margin-bottom: 16px;

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .section-title {
          font-style: normal;
          font-weight: 400;
          font-size: 14px;
          color: #9e9e9e;
        }

        // .tag-optional {
        //   background: #444;
        //   color: #ccc;
        //   padding: 2px 6px;
        //   border-radius: 4px;
        //   font-size: 12px;
        // }
      }

      .card {
        padding: 24px;
        background-color: #1f1f1f;
        border: 1px solid #353535;
        border-radius: 10px;

        .card-header {
          display: flex;
          justify-content: space-between;

          .card-left {
            .check-box {
              display: flex;
              align-items: center;

              .card-title {
                font-weight: 600;
                font-size: 18px;
                color: #f8f8f8;
                margin-left: 10px;
              }

              .icon-token {
                font-size: 18px;
                color: #ffb524;
              }

              ::v-deep .el-checkbox {
                .el-checkbox__input {
                  .el-checkbox__inner {
                    width: 18px;
                    height: 18px;
                    background-color: #353535;
                    border: 1px solid #9e9e9e;

                    &::after {
                      border: 2px solid #262626;
                      border-left: 0px;
                      border-top: 0px;
                    }
                  }
                }

                .is-checked,
                .is-indeterminate {
                  .el-checkbox__inner {
                    background-color: #2fb54e;
                    border-color: #2fb54e;

                    &::after {
                      left: 5px;
                      top: 3px;
                    }
                  }
                }
              }
            }

            .description {
              margin: 6px 0px 6px 27px;
              font-style: normal;
              font-weight: 400;
              font-size: 14px;
              color: #7b7b7b;
              text-align: left;
            }

            .feature-list {
              list-style: none;
              padding: 0;
              margin: 6px 0px 6px 27px;

              li {
                display: flex;
                align-items: center;
                margin-bottom: 8px;
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                color: #ccc;

                .feature-icon {
                  margin-right: 8px;
                  background-color: transparent;
                  border: none;
                  font-weight: bold;
                  color: #2fb54e;
                }
              }
            }

            .card-tag {
              display: flex;
              gap: 10px;
              margin: 6px 0px 6px 27px;

              .el-tag {
                height: 24px;
                line-height: 24px;
                background-color: #353535;
                border-radius: 4px;
                border: none;
                font-size: 12px;
                color: #9e9e9e;
                .icon {
                  margin-right: 3px;
                }
              }
            }
          }

          .card-right {
            display: flex;
            flex-direction: column;
            align-items: flex-end;

            .price-block {
              text-align: right;
              margin-bottom: 15px;

              .price-font {
                font-style: normal;
                font-weight: 600;
                font-size: 32px;
                color: #fff;
              }

              .price-unit {
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                color: #7b7b7b;
              }
            }

            .price-btn {
              background-color: #2fb54e !important;
              border: 1px solid #2fb54e !important;
              font-style: normal;
              font-weight: 400;
              font-size: 14px;
              color: #fff !important;
              height: 32px;
              line-height: 0px !important;
              padding: 10px 16px;
              border-radius: 4px;

              &.is-active {
                background-color: transparent !important;
                color: #2fb54e !important;
              }
            }

            .purchase-token {

              .token-price {
                display: flex;
                align-items: center;
                margin-top: 10px;
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                color: #fff;

                .single-price {
                  font-weight: 600;
                  font-size: 32px;
                }
              }

              .token-count {
                display: flex;
                justify-content: flex-end;

                ::v-deep .el-input-number {

                  .el-input-number__decrease,
                  .el-input-number__increase {
                    background-color: #353535;
                    border-color: #595959;
                    color: #dbdbdb;

                    &.is-disabled {
                      background-color: #434343 !important;
                      color: #595959 !important;
                    }

                    .el-icon-arrow-up,
                    .el-icon-arrow-down {
                      color: #dbdbdb;
                      transform: scale(1);
                    }
                  }

                  .el-input__inner {
                    background-color: #353535;
                    border-radius: 4px;
                    outline: none;
                    box-sizing: border-box;
                    padding: 0 5px;
                    color: #fff;
                    text-align: center;
                    border: 1px solid #595959;
                  }
                }
              }

              .token-total {
                margin-top: 5px;
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                color: #7b7b7b;
                text-align: right;
              }
            }
          }
        }

        .card-bottom {
          .auto-recharge {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: calc(100% - 60px);
            margin: 15px 0px;
            padding: 10px 30px;
            background-color: #262626;
            border-radius: 10px;

            .recharge-operate {
              display: flex;
              align-items: center;

              .recharge-info {
                margin-left: 10px;
                text-align: left;

                .recharge-title {
                  margin-bottom: 5px;
                  font-style: normal;
                  font-weight: 700;
                  font-size: 14px;
                  color: #F8F8F8;
                }

                .recharge-text {
                  font-style: normal;
                  font-weight: 400;
                  font-size: 12px;
                  color: #7b7b7b;
                }
              }
            }

            .recharge-tip {
              font-style: normal;
              font-weight: 400;
              font-size: 12px;
              color: #7b7b7b;

            }
          }

          .service-price-list {
            .list-title {
              display: flex;
              align-items: center;
              font-style: normal;
              font-weight: 600;
              font-size: 18px;
              color: #F8F8F8;
              cursor: pointer;
              user-select: none;

              .icon-tag {
                font-size: 18px;
                color: #3892f3;
              }

              .card-title {
                font-weight: 600;
                font-size: 18px;
                color: #f8f8f8;
                margin-left: 10px;
              }

              .service-price-discount-badge {
                background-color: rgba(47, 181, 78, 0.2);
                color: #2fb54e;
                font-size: 12px;
                font-weight: 400;
                padding: 2px 8px;
                border-radius: 10px;
                margin-left: 10px;
              }

              .collapse-icon {
                margin-left: auto;
                color: #9e9e9e;
                font-size: 14px;
              }
            }

            .price-table {
              padding: 20px 70px 0px;
              text-align: left;

              .table-header {
                display: flex;
                padding-bottom: 10px;
                border-bottom: 1px solid #353535;
                font-style: normal;
                font-weight: 400;
                font-size: 14px;
                color: #9e9e9e;

                .col-app,
                .col-rate {
                  width: 50%;
                }
              }

              .table-row {
                display: flex;
                padding: 12px 0;
                border-bottom: 1px solid #3a3a3a;

                &:last-child {
                  border-bottom: none;
                }

                .col-app {
                  width: 50%;
                  font-style: normal;
                  font-weight: 700;
                  font-size: 14px;
                  color: #fff;
                }

                .col-rate {
                  width: 50%;
                  display: flex;
                  align-items: center;

                  .rate-bold {
                    font-weight: 600;
                    font-size: 16px;
                    color: #fff;
                  }

                  .rate-unit {
                    font-weight: 400;
                    font-size: 14px;
                    color: #9e9e9e;
                    margin-left: 5px;
                  }

                  .rate-discounted {
                    font-weight: 600;
                    font-size: 16px;
                    color: #2fb54e;
                  }

                  .rate-original {
                    font-weight: 400;
                    font-size: 14px;
                    color: #595959;
                    text-decoration: line-through;
                    margin-left: 10px;
                  }

                  .discount-badge {
                    background-color: rgba(47, 181, 78, 0.2);
                    color: #2fb54e;
                    font-size: 12px;
                    padding: 2px 6px;
                    border-radius: 10px;
                    margin-left: 10px;
                    font-weight: 400;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .footer {
    background-color: #1a1a1a;
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #353535;
    border-radius: 0px 0px 10px 10px;

    &.footer-highlight {
      background-color: #1f2a1f;
      border-top: 1px solid #2fb54e33;
    }

    .footer-left {
      display: flex;
      align-items: center;
      gap: 20px;

      .total-payment {
        .label {
          margin-bottom: 2px;
          font-size: 14px;
          color: #7b7b7b;
        }

        .value {
          font-style: normal;
          font-weight: 600;
          font-size: 32px;
          color: #fff;
        }
      }

      .line {
        width: 1px;
        height: 40px;
        background-color: #353535;
      }

      .payment-details,
      .recharge-info {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        color: #7b7b7b;

        .total-price {
          margin-left: 5px;
          font-weight: 700;
          color: #fff;
          float: right;
        }

        .white-text {
          margin-left: 5px;
          color: #fff;
        }
      }
      .detail-line {
        width: 100%;
        text-align: left;
      }
    }

    .pay-btn {
      background-color: #2fb54e;
      border-color: #2fb54e;
      font-weight: 600;
      padding: 10px 25px;

      i {
        margin-right: 5px;
      }
    }
  }
}

.el-dialog__wrapper {
  ::v-deep .el-dialog.checkout-dialog {
    background: #fff;
    width: 400px;
    border-radius: 12px !important;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.15), 0 8px 40px rgba(0, 0, 0, 0.8) !important;
    overflow: hidden;
    .el-dialog__header,
    .el-dialog__body {
      padding: 0;
    }
  }
}
</style>
